import forge from 'node-forge';

/**
 * Valida un certificado PKCS#12 (.pfx / .p12) con su contraseña
 * @param {string} base64Certificate - Certificado en base64
 * @param {string} password - Contraseña del certificado
 * @returns {Object} { isValid, error, certificateInfo }
 */
export const validateCertificate = (base64Certificate, password) => {
    try {
        // Convertir base64 a bytes
        const certificateBytes = forge.util.decode64(base64Certificate);
        
        // Intentar cargar el certificado con la contraseña
        const p12Asn1 = forge.asn1.fromDer(certificateBytes);
        const p12 = forge.pkcs12.pkcs12FromAsn1(p12Asn1, password);
        
        // Obtener información del certificado
        const bags = p12.getBags({ bagType: forge.pki.oids.certBag });
        const certBag = bags[forge.pki.oids.certBag][0];
        
        if (!certBag) {
            return {
                isValid: false,
                error: 'No se encontró un certificado válido en el archivo',
                certificateInfo: null
            };
        }
        
        const cert = certBag.cert;
        const now = new Date();
        const notBefore = cert.validity.notBefore;
        const notAfter = cert.validity.notAfter;
        
        // Validar fechas
        const isExpired = now > notAfter;
        const isNotYetValid = now < notBefore;
        
        // Extraer información del certificado
        const subject = cert.subject.attributes.reduce((acc, attr) => {
            acc[attr.shortName] = attr.value;
            return acc;
        }, {});
        
        const issuer = cert.issuer.attributes.reduce((acc, attr) => {
            acc[attr.shortName] = attr.value;
            return acc;
        }, {});
        
        const certificateInfo = {
            commonName: subject.CN || 'N/A',
            organization: subject.O || 'N/A',
            organizationalUnit: subject.OU || 'N/A',
            issuer: issuer.CN || 'N/A',
            validFrom: notBefore.toLocaleDateString('es-ES'),
            validTo: notAfter.toLocaleDateString('es-ES'),
            serialNumber: cert.serialNumber,
            isExpired,
            isNotYetValid,
            daysUntilExpiration: Math.ceil((notAfter - now) / (1000 * 60 * 60 * 24))
        };
        
        return {
            isValid: true,
            error: null,
            certificateInfo,
            warnings: [
                ...(isExpired ? ['⚠️ El certificado ha expirado'] : []),
                ...(isNotYetValid ? ['⚠️ El certificado aún no es válido'] : []),
                ...(certificateInfo.daysUntilExpiration < 30 && !isExpired ? 
                    [`⚠️ El certificado expirará en ${certificateInfo.daysUntilExpiration} días`] : [])
            ]
        };
        
    } catch (error) {
        console.error('Error validando certificado:', error);
        
        // Identificar el tipo de error
        let errorMessage = 'Error al validar el certificado';
        
        if (error.message.includes('Invalid password') || 
            error.message.includes('MAC verification failed') ||
            error.message.includes('HMAC verification failed')) {
            errorMessage = 'Contraseña incorrecta para el certificado';
        } else if (error.message.includes('ASN.1')) {
            errorMessage = 'El archivo no es un certificado válido';
        }
        
        return {
            isValid: false,
            error: errorMessage,
            certificateInfo: null
        };
    }
};

/**
 * Validación rápida (solo verifica si la contraseña es correcta)
 * @param {string} base64Certificate - Certificado en base64
 * @param {string} password - Contraseña del certificado
 * @returns {boolean} true si la contraseña es correcta
 */
export const isPasswordCorrect = (base64Certificate, password) => {
    const result = validateCertificate(base64Certificate, password);
    return result.isValid;
};
